import React from "react";
import { Link } from "react-router-dom";
import { X, MessageCircle, Heart, MapPin, ExternalLink } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const RISK_COLORS = { green: "#10B981", yellow: "#EAB308", red: "#EF4444" };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Bottom-sheet preview shown when a marker is selected. */
export default function MarkerPreviewCard({ report, onClose }) {
  const { t } = useI18n();
  if (!report) return null;
  const photo = report.photos && report.photos[0];
  const catLabel = t(`cat_${report.category}`) || report.category;
  const color = RISK_COLORS[report.risk_level] || "#9CA3AF";

  return (
    <div className="absolute inset-x-0 bottom-0 z-[1100] px-3 pb-3 pointer-events-none" data-testid="marker-preview">
      <div className="mx-auto max-w-md pointer-events-auto glass-panel rounded-2xl overflow-hidden fade-in shadow-2xl">
        <div className="relative">
          {photo ? (
            <img src={`${BACKEND_URL}/api/files/${photo}`} alt={report.subcategory} className="w-full h-40 object-cover" data-testid="preview-photo" />
          ) : (
            <div className="w-full h-40 bg-gradient-to-br from-emerald-100 to-emerald-200 flex items-center justify-center text-emerald-800" data-testid="preview-placeholder">
              <MapPin size={40} />
            </div>
          )}
          <button
            data-testid="preview-close"
            onClick={onClose}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"
            aria-label={t("close") || "Chiudi"}
          >
            <X size={16} />
          </button>
          <div className="absolute top-2 left-2 inline-flex gap-1.5">
            <Badge className="capitalize border-0" style={{ backgroundColor: color, color: "#fff" }}>{report.risk_level}</Badge>
            <Badge className="bg-white/90 text-slate-800 border-0 capitalize">{t(`st_${report.status}`) || report.status?.replace(/_/g," ")}</Badge>
          </div>
        </div>
        <div className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">{catLabel}</div>
          <div className="font-heading font-semibold text-slate-900 text-base mt-0.5 capitalize">{t(`sub_${report.subcategory}`) || report.subcategory?.replace(/_/g," ")}</div>
          <div className="text-sm text-slate-600 mt-1 line-clamp-2">{report.description}</div>
          <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1"><MapPin size={11} /> {report.lat?.toFixed(3)}, {report.lng?.toFixed(3)}</span>
            {report.message_count > 0 && <span className="inline-flex items-center gap-1"><MessageCircle size={11} /> {report.message_count}</span>}
            <span>· {report.confirmations || 0} {t("confirmations_short")}</span>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Link to={`/report/${report.report_id}`} data-testid="preview-open" className="flex-1">
              <Button className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white text-sm">
                <ExternalLink size={14} className="mr-1.5" /> {t("open_details") || "Apri dettagli"}
              </Button>
            </Link>
            <Link to={`/report/${report.report_id}#chat`} data-testid="preview-chat" className="shrink-0">
              <Button variant="outline" size="icon" className="rounded-full hover:bg-slate-900 hover:text-white h-10 w-10"><MessageCircle size={16} /></Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
